import { matchPropertiesBySearchString } from '@reconcept-platform/shared/util';
import { cloneDeep, groupBy, uniqBy } from 'lodash-es';
import { type TreeNode } from './tree-node.interface';

export function getParentsFromTreeForValue<T extends { id: string }>(
  selected: T,
  options: TreeNode<T>[],
  withMathchingNode = true,
): TreeNode<T>[] {
  function findPathToNode(selected: T, node: TreeNode<T>, track: TreeNode<T>[]) {
    if (!node || !selected) {
      return false;
    }

    // @ts-expect-error TS18048 possibly undefined
    if (selected.id === node.value.id) {
      if (withMathchingNode) {
        track.push(node);
      }
      return true;
    }

    if (!node.children) {
      return false;
    }

    for (const child of node.children) {
      if (findPathToNode(selected, child, track)) {
        track.push(node);
        return true;
      }
    }

    return false;
  }

  const path: any[] = [];
  (options ?? []).forEach((option) => {
    findPathToNode(selected, option, path);
  });

  return path;
}

export function filterTree(query: string, options: TreeNode[]): any[] {
  return filterTreeByFunction((node) => matchPropertiesBySearchString(node, query, ['label']), options);
}

export function filterTreeByFunction<T extends { id: string }>(
  filterFn: (val: TreeNode<T>) => boolean,
  tree: TreeNode<T>[],
): TreeNode<T>[] {
  // @ts-expect-error TS7023 implicit any return
  function filterByFunction(node: TreeNode<T>) {
    if (!node) {
      return false;
    }

    if (filterFn(node)) {
      return true;
    }

    if (node.children) {
      node.children = node.children.filter((node) => filterByFunction(node));
      return node.children.length;
    }

    return false;
  }

  // the filter mutates the tree, so we need to clone it to keep the original intact
  return cloneDeep(tree).filter((node) => filterByFunction(node));
}

/**
 * Appends a value to a list if it does not exist yet, so we can process it in transformListToTree
 *
 * @param list the list to append to, if the value is not already in the list by ID
 * @param value the value to add, which has a parent property we can traverse
 * @param parentKey is the key in the value object that contains the parent, eg ancestors in Organization
 */
export function appendValueIfNotExists<T extends { id?: string; parentId?: string }, U extends { id?: string }>(
  list: T[],
  value: U,
  parentKey: keyof U,
) {
  return list.find((item) => item.id === value.id) ? list : [...flattenByParentKey(value, parentKey), ...list];
}

function flattenByParentKey<T extends { id?: string; parentId?: string }>(item: T, parentKey: keyof T): T[] {
  if (!Array.isArray(item[parentKey])) {
    return [item];
  }
  return [item, ...(item[parentKey] ?? []).flatMap((it) => flattenByParentKey(it, parentKey))];
}

/**
 * Transforms a flat list of items with id & parentId
 * into a tree, where the items with parentId becomes a child of the parent
 *
 * @param list: the list to transform
 */
export function transformListToTree<T extends { id: string; parentId?: string }>(
  list: T[],
  options: { labelProp: keyof T },
): TreeNode<T>[] {
  const uniqueList = uniqBy(list, 'id');
  const byParent = groupBy(uniqueList, (item) => item.parentId ?? '');

  const allIds = uniqueList.map((item) => item.id);
  // @ts-expect-error TS2345 argument not assignable
  const roots = uniqueList.filter((item) => !allIds.includes(item.parentId));

  const sortByLabel = (a: TreeNode<T>, b: TreeNode<T>) => a.label.localeCompare(b.label);

  const tree: TreeNode<T>[] = roots
    .map((item) => ({
      label: item[options.labelProp] as string,
      value: item,
    }))
    .sort(sortByLabel);

  const todos = [...tree];

  while (todos.length) {
    const parent = todos.shift();
    // @ts-expect-error TS18048 possibly undefined
    const children = byParent[parent.value.id] || [];
    if (children.length) {
      // @ts-expect-error TS18048 possibly undefined
      parent.children = children
        .map((item) => ({
          label: item[options.labelProp] as string,
          value: item,
        }))
        .sort(sortByLabel);

      // @ts-expect-error TS18048 possibly undefined
      todos.push(...parent.children);
    }
  }

  return tree;
}

export function shortenTree<T extends { id: string }>(root: TreeNode<T>[]): TreeNode<T>[] {
  return (root ?? []).map((node) => {
    // Try to collapse chain of single-child nodes ending in a leaf
    const breadcrumbs: string[] = [];
    const chain: TreeNode<T>[] = [node];
    let current = node;
    while (current.children?.length === 1) {
      const child = current.children[0];
      breadcrumbs.push(child.label);
      chain.push(child);
      current = child;
    }
    // Only collapse if the last node in the chain is a leaf
    if (chain.length > 1 && (!current.children || current.children.length === 0)) {
      return {
        ...node,
        value: current.value,
        breadcrumbs,
        children: [],
      };
    }
    // Otherwise, recursively process children
    return {
      ...node,
      children: node.children ? shortenTree(node.children) : [],
    };
  });
}

export function markSelectedPath<T extends { id: string }>(value: T, options?: TreeNode<T>[]): TreeNode<T>[] {
  if (!options) return [];
  const parents = getParentsFromTreeForValue(value, options);
  return options.map((option) => {
    // @ts-expect-error TS18048 possibly undefined
    const selected = !!parents.find((p) => p.value.id === option.value.id);
    const children = option.children ? markSelectedPath(value, option.children) : undefined;
    return {
      ...option,
      selected,
      ...(children ? { children } : {}),
    };
  });
}
